import { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../lib/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { useToast } from '../components/ui/use-toast';
import { Camera, Edit2, Save, X, ArrowLeft, MessageSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../lib/utils';
import { Dialog, DialogContent, DialogClose } from '../components/ui/dialog';
import { Toaster } from '../components/ui/toaster';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';


interface ProfileProps {
  publicUser?: any;
  className?: string;
}

export default function Profile({ publicUser, className }: ProfileProps) {
  const { auth, isGuest, openAuthPrompt } = useAppContext();
  const { toast } = useToast();
  const [location, setLocation] = useLocation();
  // Use publicUser if provided, otherwise fallback to auth.user
  const user = publicUser || auth.user;
  const [isEditing, setIsEditing] = useState(false);
  const [bio, setBio] = useState(user?.bio || '');
  const [city, setCity] = useState(user?.city || '');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [showImageError, setShowImageError] = useState(false);
  const [showPasswordUpdated, setShowPasswordUpdated] = useState(false);
  const [showProfileUpdated, setShowProfileUpdated] = useState(false);
  const [showProfilePictureUpdated, setShowProfilePictureUpdated] = useState(false);

  // Friendship status for message button
  const isOwnProfile = !publicUser || auth.user?.id === publicUser?.id;
  const { data: friendship, isLoading: loadingFriendship } = useQuery({
    queryKey: ['friendship-status', auth.user?.id, publicUser?.id],
    queryFn: async () => {
      if (!auth.user?.id || !publicUser?.id || isOwnProfile) return null;
      const { data, error } = await supabase
        .from('friendships')
        .select('status')
        .or(`and(user_id.eq.${auth.user.id},friend_id.eq.${publicUser.id}),and(user_id.eq.${publicUser.id},friend_id.eq.${auth.user.id})`)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!publicUser && !!auth.user?.id && !isOwnProfile,
  });

  // Automatically clear imageError after 5 seconds, then fade out
  useEffect(() => {
    if (imageError) {
      setShowImageError(true);
      const timer = setTimeout(() => {
        setShowImageError(false);
        // Remove the error after fade-out (500ms)
        setTimeout(() => setImageError(null), 500);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [imageError]);

  useEffect(() => {
    // If viewing own profile while guest, prompt auth with profile-specific message
    if (isGuest && !publicUser) {
      openAuthPrompt('Create an account or sign in to make changes to your profile page.');
    }
    if (window.localStorage.getItem('passwordUpdated')) {
      setShowPasswordUpdated(true);
      window.localStorage.removeItem('passwordUpdated');
      setTimeout(() => setShowPasswordUpdated(false), 5000);
    }
  }, []);

  const handleEditClick = () => {
    if (isGuest) {
      openAuthPrompt('Create an account or sign in to make changes to your profile page.');
      return;
    }
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    if (isGuest) {
      openAuthPrompt('Create an account or sign in to make changes to your profile page.');
      return;
    }
    if (!user?.id) {
      logError('Profile Update', new Error('User not authenticated'), { user });
      toast({
        title: 'Error',
        description: 'User not authenticated. Please try logging in again.',
        variant: 'destructive',
      });
      return;
    }
    if (city.length > 64) {
      toast({
        title: 'Error',
        description: 'City/location must be 64 characters or less.',
        variant: 'destructive',
      });
      return;
    }

    try {

      const { data, error: upsertError } = await supabase
        .from('users')
        .upsert(
          {
            id: user.id,
            bio,
            city,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'id' }
        )
        .select()
        .single();

      if (upsertError) {
        logError('Profile Update - Supabase Upsert Error', upsertError, { userId: user.id });
        throw upsertError;
      }

      // Update local user state
      const updatedUser = {
        ...user,
        bio,
        city,
      };
      auth.setUser(updatedUser);



      setShowProfileUpdated(true);
      setTimeout(() => setShowProfileUpdated(false), 5000);
      setIsEditing(false);
    } catch (error: any) {
      logError('Profile Update', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to update profile. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleCancelClick = () => {
    setBio(user?.bio || '');
    setCity(user?.city || '');
    setIsEditing(false);
  };

  const handleImageClick = () => {
    if (isGuest) {
      openAuthPrompt('Create an account or sign in to make changes to your profile page.');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setImageError('No file selected. Please try again.');
      return;
    }

    // Validate file size (max 4MB) BEFORE opening cropper
    const MAX_FILE_SIZE_BYTES = 4 * 1024 * 1024; // 4MB in bytes
    const MAX_FILE_SIZE_MB = MAX_FILE_SIZE_BYTES / (1024 * 1024);

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setImageError(`Image too large. Please select an image smaller than ${MAX_FILE_SIZE_MB}MB.`);
      // Clear the file input to prevent re-selection
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setImageError(null);
    setSelectedImage(file);
    setShowCropper(true);
  };

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropSave = async () => {
    if (!selectedImage || !croppedAreaPixels) return;
    const imageSrc = URL.createObjectURL(selectedImage);
    const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
    if (!croppedBlob) return;
    // Now upload croppedBlob as the new profile picture
    await uploadCroppedImage(croppedBlob);
    setShowCropper(false);
    setSelectedImage(null);
    // Clear the file input after successful crop
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadCroppedImage = async (croppedBlob: Blob) => {
    if (!user?.id) return;
    try {
      setIsUploading(true);
      // Delete old avatar if it exists
      if (user.avatar) {
        try {
          const url = new URL(user.avatar);
          const pathParts = url.pathname.split('/');
          const bucketIndex = pathParts.indexOf('avatars');
          if (bucketIndex !== -1) {
            const filePath = pathParts.slice(bucketIndex + 1).join('/');
            if (filePath) {
              await supabase.storage.from('avatars').remove([filePath]);
      
            }
          }
        } catch (deleteError) {
          // Failed to delete old avatar
        }
      }
      const fileName = `${user.id}/${Date.now()}.jpeg`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, croppedBlob, {
          cacheControl: '3600',
          upsert: true,
          contentType: 'image/jpeg',
        });
      if (uploadError) {
        logError('Image Upload - Storage Error', uploadError, { fileName });
        throw uploadError;
      }
      // Get the public URL
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(fileName);
      // Update the user's profile with the new avatar URL
      const { error: upsertError } = await supabase
        .from('users')
        .upsert(
          {
            id: user.id,
            avatar: publicUrl,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'id' }
        )
        .select()
        .single();
      if (upsertError) {
        logError('Image Upload - Profile Upsert Error', upsertError, { userId: user.id });
        throw upsertError;
      }
      // Update local user state
      if (user) {
        const updatedUser = {
          ...user,
          avatar: publicUrl,
        };
        auth.setUser(updatedUser);
      }
      setShowProfilePictureUpdated(true);
      setTimeout(() => setShowProfilePictureUpdated(false), 5000);
    } catch (error: any) {
      logError('Image Upload', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to update profile picture. Please try again.',
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100">
      <div className="px-4 py-4 max-w-md mx-auto">
        <Card className="w-full backdrop-blur-sm bg-white/80 shadow-xl border-0 rounded-2xl ring-0 outline-none shadow-[0_8px_30px_rgba(0,0,0,0.12)] relative">
          <CardContent className="p-6">
            {/* Back Button */}
            <button
              onClick={() => {
                const params = new URLSearchParams(window.location.search);
                const from = params.get('from');
                if (from === 'messages') {
                  // When arriving from a message thread, go back to connections list
                  window.location.assign('/friends');
                  return;
                }
                if (window.history.length > 1) {
                  window.history.back();
                } else {
                  window.location.assign('/friends');
                }
              }}
              className="absolute left-4 top-4 z-10 bg-white/80 rounded-full p-2 shadow hover:bg-blue-50 transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5 text-blue-700" />
            </button>
            {/* Profile Header */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative group">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-blue-100 to-blue-200">
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-blue-600">
                      <Camera className="h-12 w-12" />
                    </div>
                  )}
                </div>
                {!publicUser && (
                  <>
                    <button
                      onClick={handleImageClick}
                      disabled={isUploading}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUploading ? (
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
                      ) : (
                        <Camera className="h-8 w-8 text-white" />
                      )}
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      accept="image/*"
                      className="hidden"
                      disabled={isUploading}
                    />
                  </>
                )}
              </div>
              <div className="mt-4 flex flex-col items-center gap-2 w-full">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent text-center">
                  {user?.name}
                </h2>
                {isEditing ? (
                  <Input
                    value={city}
                    onChange={(e) => {
                      if (e.target.value.length <= 64) setCity(e.target.value);
                    }}
                    placeholder="Enter your city"
                    className="w-full max-w-xs text-center transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border border-gray-200 shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600"
                    maxLength={64}
                  />
                ) : (
                  <p className="text-sm text-gray-600 break-all whitespace-pre-wrap">{city || 'No city set'}</p>
                )}
                {!publicUser && <p className="text-sm text-gray-600 mt-1">{user?.email}</p>}
                {/* Message Button for friends only */}
                {publicUser && !isOwnProfile && friendship?.status === 'accepted' && (
                  <Button
                    variant="outline"
                    className="mt-3 flex items-center gap-2"
                    onClick={() => setLocation(`/messages?user=${publicUser.id}&from=profile`)}
                  >
                    <MessageSquare className="h-5 w-5" />
                    Message
                  </Button>
                )}
              </div>
            </div>

            {/* Bio Section */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">About</h3>
                {!publicUser && (
                  !isEditing ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleEditClick}
                      className="text-primary"
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelClick}
                        className="text-gray-500"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleSaveClick}
                        className="text-primary"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save
                      </Button>
                    </div>
                  )
                )}
              </div>
              {isEditing ? (
                <Textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell us about yourself..."
                  className="min-h-[100px] resize-none transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border border-gray-200 shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600"
                />
              ) : (
                <p className="text-gray-600 whitespace-pre-wrap break-words">
                  {bio || 'No bio yet. Click edit to add one!'}
                </p>
              )}
              {imageError && (
                <div
                  className={`mt-4 p-4 bg-red-50 rounded-lg flex items-center gap-2 animate-fade-in transition-opacity duration-500 ${showImageError ? 'opacity-100' : 'opacity-0'}`}
                  data-testid="image-error"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-red-500 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"
                    />
                  </svg>
                  <p className="text-sm text-red-600 font-medium">{imageError}</p>
                </div>
              )}
            </div>

            {/* Stats Section removed per request */}
          </CardContent>
        </Card>
      </div>
      {showCropper && selectedImage && (
        <Dialog open={showCropper} onOpenChange={setShowCropper}>
          <DialogContent className="flex items-center justify-center min-h-screen p-0 bg-transparent shadow-none border-none">
            <div className="w-[90vw] max-w-xs h-[90vw] max-h-[400px] flex flex-col items-center justify-center bg-white rounded-2xl overflow-hidden p-4">
              <div className="relative w-full h-full flex-1 flex items-center justify-center p-2">
                <Cropper
                  image={URL.createObjectURL(selectedImage)}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  style={{
                    containerStyle: {
                      borderRadius: '9999px',
                      background: 'rgba(0,0,0,0.7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '12px',
                    },
                    cropAreaStyle: {
                      border: '2px solid #fff',
                      boxShadow: '0 0 0 9999px rgba(0,0,0,0.7)',
                      borderRadius: '9999px',
                    },
                  }}
                />
              </div>
              <div className="flex gap-4 mt-4 pb-4">
                <DialogClose asChild>
                  <Button variant="ghost">Cancel</Button>
                </DialogClose>
                <Button onClick={handleCropSave} disabled={isUploading}>
                  {isUploading ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {showPasswordUpdated && (
        <div className="mt-4 p-4 bg-green-50 rounded-lg flex items-center gap-2 animate-fade-in transition-opacity duration-500 opacity-100">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-green-500 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          <p className="text-sm text-green-600 font-medium">Your password has been changed successfully.</p>
        </div>
      )}
      {showProfileUpdated && (
        <div className="mt-4 p-4 bg-green-50 rounded-lg flex items-center gap-2 animate-fade-in transition-opacity duration-500 opacity-100">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-green-500 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          <p className="text-sm text-green-600 font-medium">Your profile has been updated successfully.</p>
        </div>
      )}
      {showProfilePictureUpdated && (
        <div className="mt-4 p-4 bg-green-50 rounded-lg flex items-center gap-2 animate-fade-in transition-opacity duration-500 opacity-100">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-green-500 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          <p className="text-sm text-green-600 font-medium">Your profile picture has been updated successfully.</p>
        </div>
      )}
      <Toaster />
    </div>
  );
}